export const validateAdqlQuery = (query: string): string => {
  if (!query.trim()) {
    return 'Query cannot be empty';
  }

  const upperQuery = query.trim().toUpperCase();

  // Check for basic SQL injection patterns
  const suspiciousPatterns = [
    /;/, // Semicolons (potential for multiple statements)
    /--/, // SQL comments
    /\/\*|\*\//, // Multi-line comments
    /\bUNION\b.*?\bSELECT\b/i, // UNION SELECT attacks
    /\bEXEC\b/i, // EXEC statements
    /\bXP_\w+/i, // Extended stored procedures
    /\bDROP\b/i, // DROP statements
    /\bDELETE\b/i, // DELETE statements
    /\bUPDATE\b/i, // UPDATE statements
    /\bINSERT\b/i, // INSERT statements
    /\bALTER\b/i, // ALTER statements
  ];

  if (suspiciousPatterns.some((pattern) => pattern.test(query))) {
    return 'Query contains potentially unsafe patterns';
  }

  // Check for unmatched quotes
  const singleQuotes = (query.match(/'/g) || []).length;
  if (singleQuotes % 2 !== 0) {
    return 'Unmatched single quotes in query';
  }

  const doubleQuotes = (query.match(/"/g) || []).length;
  if (doubleQuotes % 2 !== 0) {
    return 'Unmatched double quotes in query';
  }

  // Check for unmatched parentheses
  let openParenCount = 0;
  for (let i = 0; i < query.length; i++) {
    if (query[i] === '(') {
      openParenCount++;
    } else if (query[i] === ')') {
      openParenCount--;
      if (openParenCount < 0) {
        return 'Unmatched parentheses in query';
      }
    }
  }
  if (openParenCount !== 0) {
    return 'Unmatched parentheses in query';
  }

  // Basic structure checks
  if (!upperQuery.startsWith('SELECT')) {
    return 'Query must start with SELECT statement';
  }
  if (!upperQuery.includes('FROM')) {
    return 'Query must include FROM clause';
  }
  // if (!upperQuery.includes('IVOA.OBSCORE')) {
  //  return 'Query must use the IVOA.OBSCORE table';
  // }

  // Check for common error patterns
  if (/\bWHERE\s*$/.test(query)) {
    return 'Invalid query syntax: incomplete WHERE clause';
  }

  if (/WHERE\s+(AND|OR|NOT)\b/i.test(query)) {
    return 'Invalid query syntax: WHERE clause cannot be followed by AND/OR/NOT/';
  }

  if (/=\s*$/.test(query)) {
    return 'Invalid query syntax: incomplete comparison';
  }

  // Check for dangling logical operators - make sure to catch them inside parentheses too
  if (/\b(AND|OR)\s*(\)|$)/i.test(query)) {
    return 'Invalid query syntax: dangling logical operator';
  }

  // Check for mixed quote types
  if (/(['"]).*?(?!\1)(['"])/.test(query)) {
    return 'Unmatched single quotes in query';
  }

  // Validate ADQL geometric functions
  if (upperQuery.includes('POINT') || upperQuery.includes('CIRCLE')) {
    const coordParam = /(?:[\d.+-]+|"[^"]+"|'[^']+'|\w+)/;
    const pointPattern = new RegExp(
      `POINT\\s*\\(\\s*'ICRS'\\s*,\\s*${coordParam.source}\\s*,\\s*${coordParam.source}\\s*\\)`,
      'i',
    );
    const circlePattern = new RegExp(
      `CIRCLE\\s*\\(\\s*'ICRS'\\s*,\\s*${coordParam.source}\\s*,\\s*${coordParam.source}\\s*,\\s*[\\d.+-]+\\s*\\)`,
      'i',
    );

    if (upperQuery.includes('POINT')) {
      const pointMatch = query.match(/POINT\s*\([^)]+\)/i);
      if (!pointMatch || !pointPattern.test(pointMatch[0])) {
        return "Invalid POINT function syntax. Must be: POINT('ICRS', ra, dec)";
      }
    }

    if (upperQuery.includes('CIRCLE')) {
      const circleMatch = query.match(/CIRCLE\s*\([^)]+\)/i);
      if (!circleMatch || !circlePattern.test(circleMatch[0])) {
        return "Invalid CIRCLE function syntax. Must be: CIRCLE('ICRS', ra, dec, radius)";
      }
    }

    if (upperQuery.includes('CONTAINS')) {
      const containsPattern =
        /CONTAINS\s*\(\s*POINT\s*\([^)]+\)\s*,\s*CIRCLE\s*\([^)]+\)\s*\)\s*=\s*1/i;
      if (!containsPattern.test(query)) {
        return 'Invalid CONTAINS syntax. Must be: CONTAINS(POINT(...), CIRCLE(...))=1';
      }
    }
  }

  return '';
};

export const formatAdqlQuery = (query: string): string => {
  return (
    query
      .replace(/\s+/g, ' ')
      // Update POINT function to use quoted column names
      .replace(
        /POINT\s*\(\s*'ICRS'\s*,\s*s_ra\s*,\s*s_dec\s*\)/gi,
        'POINT(\'ICRS\',\"s_ra\",\"s_dec\")',
      )
      .replace(
        /CIRCLE\s*\(\s*'ICRS'\s*,\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*,\s*(\d+\.?\d*)\s*\)/gi,
        "CIRCLE('ICRS',$1,$2,$3)",
      )
      .replace(/CONTAINS\s*\(\s*/gi, 'CONTAINS(')
      .replace(/\s*\)\s*=\s*1/g, ')=1')
      .replace(/\s*,\s*/g, ',')
      .replace(/\s*\)/g, ')')
      .replace(/\(\s*/g, '(')
      .trim()
  );
};
